import { useMemo, JSX } from 'react'

import { compact, groupBy, isNil, partition, sortBy, uniqBy } from 'lodash'
import { CircleAlert, Info } from 'lucide-react'

import { UserAvatar } from '~/components/UserAvatar'
import {
  CheckboxGroupInput,
  List,
  ListItemProps,
  Tooltip,
  UserDisplay,
} from '~/components/component-library'
import { RecoverySignerDisplay } from '~/components/create-team/steps/5_RecoveryStep/RecoverySignerDisplay'
import { EoaIconWithTooltip } from '~/components/smart-accounts/EoaIconWithTooltip'
import {
  LastUsedTooltipText,
  PasskeyIconWithTooltip,
  RemovedTooltipText,
} from '~/components/smart-accounts/PasskeyIconWithTooltip'
import { SignerIcon } from '~/components/smart-accounts/SignerIcon'
import { useActiveOrg } from '~/hooks/org'
import { useRequiredAuthUser } from '~/hooks/useAuthUser'
import { useCurrentUserPasskeys } from '~/hooks/useCurrentUserPasskeys'
import { OrgEOASigner, OrgMember, OrgSigner, UserKind } from '~/types'

type EditableProps = {
  selectedSigners: OrgSigner[]
  onChange: (signer: OrgSigner, enabled: boolean) => void
}

const sortSigners = (signers: OrgSigner[]) => {
  return sortBy(signers, (signer) =>
    signer.user
      ? (signer.user.displayName ?? signer.user.email).toLowerCase()
      : new Date(signer.createdAt),
  )
}

export const SignersList = ({
  signers: unsortedSigners,
  eoaSigners,
  header,
  description,
  threshold,
  editable,
  secondaryDisplay,
  initialExpanded,
  onEditButtonClick,
  onAddNewPasskey,
}: {
  signers: OrgSigner[]
  eoaSigners: readonly OrgEOASigner[]
  header?: string
  description?: string
  editable?: EditableProps
  secondaryDisplay?: JSX.Element
  initialExpanded: boolean
  threshold?: number
  onEditButtonClick?: () => void
  onAddNewPasskey?: () => void
}) => {
  const { org } = useActiveOrg()
  const signers = useMemo(() => sortSigners(unsortedSigners), [unsortedSigners])
  const selectedSignerIds = editable?.selectedSigners.map((s) => s.id) ?? []

  // Members that have been removed from the team, but are still a signer onchain
  const removedMembers = uniqBy(
    compact(
      signers
        .filter(
          (signer) =>
            !org?.members.some((member) => member.id === signer.user?.id),
        )
        .map((signer) => signer.user),
    ),
    'id',
  )

  const memberIds = new Set(org?.members.map((m) => m.id) ?? [])
  const agentMemberIds = new Set(
    org?.members.filter((m) => m.kind === UserKind.Agent).map((m) => m.id),
  )
  const [attachedEoas, orphanEoas] = partition(
    [...eoaSigners],
    (e): e is OrgEOASigner & { userId: string } =>
      !!e.userId && memberIds.has(e.userId),
  )
  const eoasByUserId = groupBy(attachedEoas, (e) => e.userId)

  // Expand by default if there's only one team member
  const shouldExpandByDefault =
    (editable && org?.members.length === 1) || initialExpanded

  const userItems = [...(org?.members ?? []), ...removedMembers].map((user) => {
    const isRemovedMember = removedMembers.some(
      (removedMember) => removedMember?.id === user.id,
    )

    // check if the member is a signer
    const isSigner = signers.some((signer) => signer.user?.id === user.id)
    const memberEoas = eoasByUserId[user.id] ?? []
    const hasEoas = memberEoas.length > 0

    const userSigners = signers.filter((signer) => signer.user?.id === user.id)

    const selected = editable
      ? userSigners.filter((signer) => selectedSignerIds.includes(signer.id))
          .length
      : userSigners.length

    // only show all team members when the edit dialog is open
    if (!isSigner && !hasEoas && !editable) return null

    if (isSigner || hasEoas) {
      return {
        key: user.id,
        image: isRemovedMember ? (
          <div className="opacity-50">
            <UserAvatar user={user} />
          </div>
        ) : undefined,
        content: isRemovedMember ? (
          <Tooltip content="This member has been removed from the workspace, but is still a signer onchain">
            <div className="opacity-50">
              <UserDisplay
                user={user as OrgMember}
                layout="inline-text"
                hidePopover
              />
            </div>
          </Tooltip>
        ) : (
          <UserDisplay user={user as OrgMember} layout="standard" size="base" />
        ),
        meta:
          editable && (selected > 0 || hasEoas) ? (
            <SelectedSignersIcons
              signers={userSigners.filter((s) =>
                selectedSignerIds.includes(s.id),
              )}
              eoaSigners={memberEoas}
            />
          ) : (
            !editable && (
              <SignersRow signers={userSigners} eoaSigners={memberEoas} />
            )
          ),
        initialExpanded: shouldExpandByDefault,
        // Expandable editor is passkey-only; EOAs render as read-only chips
        // and are managed via the public API / CLI.
        expandableContent: editable && isSigner && (
          <UserSigners
            signers={userSigners}
            editable={editable}
            userId={user.id}
            onAddNewPasskey={onAddNewPasskey}
          />
        ),
      }
    }

    return {
      key: user.id,
      content: (
        <UserDisplay user={user as OrgMember} layout="standard" size="base" />
      ),
      actionIcon: (
        <Tooltip
          content={
            agentMemberIds.has(user.id)
              ? 'This agent signs with its own key, which is not on this account'
              : 'This member has not added a passkey yet'
          }
        >
          <Info className="size-4 text-secondary" />
        </Tooltip>
      ),
    }
  })

  const orphanEoaItems: ListItemProps[] = orphanEoas.map((signer) => ({
    key: signer.id,
    content: (
      <RecoverySignerDisplay
        address={signer.address}
        name={signer.name}
        // A user-registered EOA carries its owner's email, not the email that
        // controls it, so only show emails for Privy recovery signers.
        email={signer.userId ? undefined : signer.email}
      />
    ),
    meta: <EoaIconWithTooltip signer={signer} iconOnly />,
    actionIcon: (
      <Tooltip content="Manage this signer via CLI / MCP">
        <Info className="size-4 text-secondary" />
      </Tooltip>
    ),
  }))

  const items: ListItemProps[] = compact([
    ...userItems,
    ...orphanEoaItems,
  ]) as ListItemProps[]

  const total = signers.length + eoaSigners.length

  const footer = !isNil(threshold) ? (
    <div>
      Transactions require {threshold} of {total} approval
      {total === 1 ? '' : 's'}.
    </div>
  ) : undefined

  return (
    <List
      label={header}
      description={description}
      secondaryDisplay={secondaryDisplay}
      items={items}
      cta={
        onEditButtonClick
          ? { label: 'Add/edit signers', onClick: onEditButtonClick }
          : undefined
      }
      footer={footer}
    />
  )
}

const SignerChips = ({
  signers,
  eoaSigners,
}: {
  signers: OrgSigner[]
  eoaSigners: OrgEOASigner[]
}) => {
  const userPasskeys = useCurrentUserPasskeys()

  return (
    <>
      {signers.map((signer) => (
        <PasskeyIconWithTooltip
          key={signer.id}
          signer={signer}
          userPasskeys={userPasskeys}
        />
      ))}
      {eoaSigners.map((signer) => (
        <EoaIconWithTooltip key={signer.id} signer={signer} />
      ))}
    </>
  )
}

const SignersRow = ({
  signers,
  eoaSigners,
}: {
  signers: OrgSigner[]
  eoaSigners: OrgEOASigner[]
}) => {
  return (
    <div className="group flex -space-x-2 hover:space-x-0">
      <SignerChips signers={signers} eoaSigners={eoaSigners} />
    </div>
  )
}

const SelectedSignersIcons = ({
  signers,
  eoaSigners,
}: {
  signers: OrgSigner[]
  eoaSigners: OrgEOASigner[]
}) => {
  return (
    <div className="group/icons flex -space-x-2 hover:space-x-0">
      <SignerChips signers={signers} eoaSigners={eoaSigners} />
    </div>
  )
}

const UserSigners = ({
  signers,
  editable,
  userId,
  onAddNewPasskey,
}: {
  signers: OrgSigner[]
  editable: EditableProps | undefined
  userId: string
  onAddNewPasskey?: () => void
}) => {
  const { id: currentUserId } = useRequiredAuthUser()

  const selectedValues = editable
    ? signers
        .filter((s) => editable.selectedSigners.some((sel) => sel.id === s.id))
        .map((s) => s.id)
    : []

  const isCurrentUser = userId === currentUserId
  const showAddNewPasskey = editable && isCurrentUser && onAddNewPasskey

  const options = signers.map((signer) => {
    const lastVerifiedAt = signer.lastVerifiedAt
      ? new Date(signer.lastVerifiedAt)
      : undefined

    return {
      value: signer.id,
      display: () => (
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <SignerIcon signer={signer} size={16} />
          <span className="flex-1 truncate">{signer.name}</span>
          {shouldShowLastVerifiedDate(lastVerifiedAt) && !signer.isArchived && (
            <Tooltip
              content={
                <div className="flex flex-col gap-0.5 leading-tight">
                  <LastUsedTooltipText lastVerifiedAt={lastVerifiedAt!} />
                </div>
              }
            >
              <CircleAlert className="size-4 text-secondary" />
            </Tooltip>
          )}
          {signer.isArchived && (
            <Tooltip
              content={
                <div className="flex flex-col gap-0.5 leading-tight">
                  <RemovedTooltipText />
                </div>
              }
            >
              <CircleAlert className="size-4 text-orange-500" />
            </Tooltip>
          )}
        </div>
      ),
    }
  })

  return (
    <div className="overflow-auto rounded-md">
      <CheckboxGroupInput
        options={options}
        value={selectedValues}
        onChange={(newValues) => {
          if (!editable) return
          // Reconcile the full delta so Select-all / Deselect-all toggle every signer,
          // not just the first changed id.
          const newSet = new Set(newValues)
          const oldSet = new Set(selectedValues)
          signers.forEach((signer) => {
            const wasSelected = oldSet.has(signer.id)
            const isSelected = newSet.has(signer.id)
            if (wasSelected !== isSelected)
              editable.onChange(signer, isSelected)
          })
        }}
        cta={
          showAddNewPasskey
            ? { label: 'Add new passkey', onClick: onAddNewPasskey! }
            : undefined
        }
      />
    </div>
  )
}

const LAST_VERIFIED_DATE_OLD_MARK_TIME = 3 * 30 * 24 * 60 * 60 * 1000 // 3 months

function shouldShowLastVerifiedDate(
  lastVerifiedAt: Date | string | undefined,
): boolean {
  if (!lastVerifiedAt) return false
  const date =
    typeof lastVerifiedAt === 'string'
      ? new Date(lastVerifiedAt)
      : lastVerifiedAt
  return Date.now() - date.getTime() > LAST_VERIFIED_DATE_OLD_MARK_TIME
}
